import { Text, Tooltip } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { IconElementTag } from 'packages/common-frontend/components/Tag';
import { useQueryParams } from 'packages/common-frontend/hooks/useQueryParams';
import { Link } from '~/valleyBank/domains/components/Link';

export const CUSTOMER_URL_PARAM = 'customer';

export const generateCustomerQueryParam = (cifnum: string): string => {
  return `?${CUSTOMER_URL_PARAM}=${cifnum}`;
};

export const generateCustomerUrl = ({
  path,
  cifnum,
}: {
  path: string;
  cifnum: string;
}): string => {
  return `${path}${generateCustomerQueryParam(cifnum)}`;
};

type CustomerLinkProps = {
  children: React.ReactNode;
  cifnum: string;
  path: string;
  scrollToTop?: boolean;
  tooltipLabel?: string;
};
const CustomerLink = ({
  children,
  cifnum,
  path,
  scrollToTop = true,
  tooltipLabel,
}: CustomerLinkProps) => {
  return (
    <Tooltip
      label={tooltipLabel || 'Customer'}
      placement="auto"
      colorScheme="aurora"
      borderRadius="8px"
      p="8px 16px"
      textAlign="center"
    >
      <span>
        <Link
          to={generateCustomerUrl({
            path,
            cifnum,
          })}
          onClick={
            scrollToTop
              ? () => {
                  window.scrollTo(0, 0);
                }
              : undefined
          }
        >
          {children}
        </Link>
      </span>
    </Tooltip>
  );
};

type CustomerSummaryLinkProps = {
  children: React.ReactNode;
  cifnum: string;
  tooltipLabel?: string;
};
export const CustomerSummaryLink = ({
  children,
  cifnum,
  tooltipLabel,
}: CustomerSummaryLinkProps) => (
  <CustomerLink
    cifnum={cifnum}
    path="/valley-bank/customers/summary"
    tooltipLabel={tooltipLabel}
  >
    {children}
  </CustomerLink>
);

type CustomerTagProps = {
  cifnum: string;
  label: string;
  tooltipLabel?: string;
};
export const CustomerTag = ({
  cifnum,
  label,
  tooltipLabel,
}: CustomerTagProps) => {
  return (
    <IconElementTag icon="user">
      <CustomerSummaryLink
        cifnum={cifnum}
        tooltipLabel={tooltipLabel || 'Customer'}
      >
        <Text fontWeight="bold">{label}</Text>
      </CustomerSummaryLink>
    </IconElementTag>
  );
};

type CustomerCifNumTagProps = { cifnum: string };
export const CustomerCifNumTag = ({ cifnum }: CustomerCifNumTagProps) => {
  return (
    <IconElementTag icon="hashtag">
      <CustomerSummaryLink cifnum={cifnum} tooltipLabel="CIF Number">
        <Text fontWeight="bold">{cifnum}</Text>
      </CustomerSummaryLink>
    </IconElementTag>
  );
};

export const useCustomerUrlParam = (): {
  cifnum: string | null;
  setCifnum: (newLoanAccountId: string | null) => void;
  generateCurrentCustomerUrl: (path: string) => string;
} => {
  const navigate = useNavigate();
  const { pathname: currentPath } = useLocation();
  const query = useQueryParams();
  const cifnum = query.get(CUSTOMER_URL_PARAM);

  const setCifnum = useCallback(
    (newLoanAccountId: string | null) => {
      // @jendevelops TODO: add error message when loan account id doesn't exist
      if (newLoanAccountId) {
        navigate(
          generateCustomerUrl({
            path: currentPath,
            cifnum: newLoanAccountId,
          }),
        );
      }
    },
    [currentPath, navigate],
  );

  const _generateCurrentCustomerUrl = useCallback(
    (path: string) => generateCustomerUrl({ path, cifnum: cifnum || '' }),
    [cifnum],
  );

  return {
    cifnum,
    setCifnum,
    generateCurrentCustomerUrl: _generateCurrentCustomerUrl,
  };
};
